import type { Storage, StorageAdapter } from '../storage.types';
import { InvalidStorageSet } from './../../../errors/InvalidStorageSet';
import { formatForStorage } from './formatForStorage';
import { parseFromStorage } from './formatForStorage/storageFormat';

type CreateWebStorageParams = {
  prefix?: string;
  storageAdapter: StorageAdapter;
};

/**
 * Creates a Storage service to interact with storage adapter
 */
export const createStorage = ({
  prefix = '',
  storageAdapter,
}: CreateWebStorageParams): Storage => {
  const getPrefixedKey = (key: string) => (prefix ? `${prefix}_${key}` : key);

  return {
    getItem: async (storageKeySchema) => {
      const rawItem = await storageAdapter.getItem(
        getPrefixedKey(storageKeySchema.key)
      );

      const parsedItem = rawItem ? parseFromStorage(rawItem) : null;

      /**
       * The item saved to localStorage may be malformed.
       * In this case, we remove it and return null.
       */
      if (parsedItem !== null) {
        const parsed = storageKeySchema.schema.safeParse(parsedItem.value);

        if (parsed.success) {
          return parsed.data;
        }
      }

      /**
       * The item saved to localStorage may be malformed.
       * In this case, we remove it and return null.
       */

      await storageAdapter.removeItem(getPrefixedKey(storageKeySchema.key));

      return null;
    },

    removeItem: async (storageKeySchema) => {
      await storageAdapter.removeItem(getPrefixedKey(storageKeySchema.key));
    },

    setItem: async (storageKeySchema, value) => {
      const parsed = storageKeySchema.schema.safeParse(value);

      if (!parsed.success) {
        throw new InvalidStorageSet({
          key: storageKeySchema.key,
          value: JSON.stringify(value),
        });
      }

      const item = formatForStorage(parsed.data);

      await storageAdapter.setItem(getPrefixedKey(storageKeySchema.key), item);
    },
  };
};
